import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

function AuthCallback() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(t('auth.processing') || 'Processing...');
  const navigate = useNavigate();
  const { handleGoogleCallback } = useAuth();
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple executions (React 18 StrictMode + re-renders)
    if (processedRef.current) {
      console.log('[AuthCallback] Already processed, skipping...');
      return;
    }
    
    const processAuth = async () => {
      processedRef.current = true;
      console.log('[AuthCallback] Starting authentication process...');
      
      try {
        // Check URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const state = urlParams.get('state');
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        
        // Check for OAuth errors
        if (error) {
          console.error('[AuthCallback] OAuth error:', error);
          setStatus(t('auth.googleAuthFailed') || 'Google-Authentifizierung fehlgeschlagen. Weiterleitung zum Login...');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
          return;
        }
        
        // Validate that this is a login OAuth callback (not Gmail)
        if (state && state.startsWith('gmail_')) {
          console.log('[AuthCallback] Gmail OAuth detected, redirecting to Gmail callback handler');
          // This should not happen with proper routing, but redirect as safety
          navigate('/auth/gmail-callback' + window.location.search, { replace: true });
          return;
        }
        
        // Process regular Google login OAuth
        const userData = await handleGoogleCallback();
        if (userData) {
          let storedRedirect = null;
          try { storedRedirect = localStorage.getItem('post_auth_redirect'); } catch (_) {}
          const safeStored = storedRedirect && storedRedirect.startsWith('/') && !storedRedirect.startsWith('//') ? storedRedirect : null;
          if (safeStored) { try { localStorage.removeItem('post_auth_redirect'); } catch (_) {} }
          const targetPath = safeStored || ((!userData?.plan || userData.plan === 'free') ? '/time-tracking' : '/dashboard');
          console.log('[AuthCallback] ✅ Authentication successful, redirecting to', targetPath);
          setStatus(t('auth.successRedirecting') || 'Erfolgreich! Weiterleitung...');
          // Immediate redirect - user is already authenticated
          navigate(targetPath, { replace: true });
        } else {
          console.log('[AuthCallback] ❌ Authentication failed, redirecting to login');
          setStatus(t('auth.googleAuthFailed') || 'Google-Authentifizierung fehlgeschlagen. Weiterleitung zum Login...');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        }
      } catch (error) {
        console.error('[AuthCallback] ❌ Error during authentication:', error);
        setStatus(t('auth.googleAuthFailed') || 'Google-Authentifizierung fehlgeschlagen. Weiterleitung zum Login...');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    };

    processAuth();
  }, [navigate, handleGoogleCallback, t]); // Include dependencies

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
        <p className="text-lg text-gray-700">{status}</p>
        <p className="text-sm text-gray-500 mt-2">
          {t('auth.loginCallbackInfo') || 'Sie werden angemeldet...'}
        </p>
      </div>
    </div>
  );
}

export default AuthCallback;